import React, { useState, useEffect } from 'react'
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_PAYMENT_METHODS } from 'graphql/reports/queries'
import { ADD_BOOKING_PAYMENT_DETAILS } from 'graphql/booking/mutations'
import { Select } from 'components/select'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'

export default function PaymentInfoDialog({ open, onClose, onSubmit, bookingData, onSuccess }) {
    const { t, i18n } = useTranslation()
    const [paymentMethods, setPaymentMethods] = useState([])
    const [formData, setFormData] = useState({
        receiptDate: new Date().toISOString().split('T')[0], // Set today's date as default
        receiptNumber: '',
        cardNo: '',
        amount: bookingData?.price?.final_price || bookingData?.total_price || '',
        card_brand: '',
    })
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [formError, setFormError] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)

    const [addPaymentDetails] = useMutation(ADD_BOOKING_PAYMENT_DETAILS, {
        onCompleted: data => {
            setSubmitLoading(false)
            if (data?.addBookingPaymentDetails.code === '200' && data?.addBookingPaymentDetails.success === true) {
                setFormError('') // Clear any form error
                // Reset form fields after successful submission
                setFormData({
                    receiptDate: new Date().toISOString().split('T')[0],
                    receiptNumber: '',
                    cardNo: '',
                    amount: bookingData?.price?.final_price || bookingData?.total_price || '',
                    card_brand: '',
                })
                setDialogMessage(data?.addBookingPaymentDetails.message || t('Payment details added successfully'))
                setShowSucDialog(true)
                onClose()
                onSuccess?.(data?.addBookingPaymentDetails.message)
                setTimeout(() => {
                    setShowSucDialog(false)
                }, 2000)
            } else {
                const apiErrorMessage = data?.addBookingPaymentDetails.message || t('Failed to add payment details')
                setFormError(apiErrorMessage)
                setDialogMessage(apiErrorMessage)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            const apiErrorMessage = error.message || t('Error submitting form. Please try again.')
            setFormError(apiErrorMessage)
            setDialogMessage(apiErrorMessage)
            setShowErrDialog(true)
        },
    })

    const [getPaymentMethods] = useLazyQuery(GET_PAYMENT_METHODS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.paymentMethods) {
                // Filter out multiple payment methods
                const excludedMethods = ['WALLET', 'CASH', 'APPLE_PAY']
                const filteredMethods = data.paymentMethods.filter(method => !excludedMethods.includes(method.code))
                setPaymentMethods(filteredMethods)
            }
        },
    })

    useEffect(() => {
        getPaymentMethods()
    }, [])

    // Update amount when bookingData changes
    useEffect(() => {
        console.log('PaymentInfoDialog bookingData', bookingData)
        if (bookingData?.price?.final_price || bookingData?.total_price) {
            setFormData(prev => ({
                ...prev,
                amount: bookingData.price?.final_price || bookingData.total_price,
            }))
        }
    }, [bookingData])

    const handleSubmit = e => {
        e.preventDefault()
        setSubmitLoading(true)
        const paymentInfo = {
            no: formData.receiptNumber,
            date: formData.receiptDate,
            currency: 'BHD',
            card_no: formData.cardNo,
            card_brand: formData.card_brand,
            amount: parseFloat(formData.amount),
        }

        // Use first segment's travel date if available, otherwise fallback to metadata travel_date
        const firstSegmentTravelDate = bookingData?.trip_segments?.[0]?.travel_date || bookingData?.metadata?.travel_date
        
        addPaymentDetails({
            variables: {
                bookingId: bookingData.booking_id,
                travelDate: firstSegmentTravelDate,
                paymentInfo: paymentInfo,
            },
        })
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} className="relative z-10">
                <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <div className="absolute right-0 top-0 pr-4 pt-4">
                                <button
                                    type="button"
                                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                                    onClick={onClose}
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            <div className="sm:flex sm:items-start">
                                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900 mb-4"
                                    >
                                        {t('Add Payment Information')}
                                    </Dialog.Title>
                                    <form onSubmit={handleSubmit}>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {t('Receipt Date')} <span className="text-red/90">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                                                    value={formData.receiptDate}
                                                    onChange={e =>
                                                        setFormData({ ...formData, receiptDate: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {t('Payment Method')} <span className="text-red/90">*</span>
                                                </label>
                                                <Select
                                                    value={formData.card_brand}
                                                    onChange={e =>
                                                        setFormData({ ...formData, card_brand: e.target.value })
                                                    }
                                                    className="mt-1 block w-full"
                                                    required
                                                >
                                                    <option value="">{t('Select Payment Method')}</option>
                                                    {paymentMethods.map(method => (
                                                        <option key={method.code} value={method.code}>
                                                            {i18n.language === 'ar'
                                                                ? method.description.ar
                                                                : method.description.en}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {t('Receipt Number')} <span className="text-red/90">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                                                    value={formData.receiptNumber}
                                                    onChange={e =>
                                                        setFormData({ ...formData, receiptNumber: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {t('Card Number (Last 4 Digit)')}{' '}
                                                    <span className="text-red/90">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    maxLength={16}
                                                    pattern="[0-9]*"
                                                    inputMode="numeric"
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                                                    value={formData.cardNo}
                                                    onChange={e => setFormData({ ...formData, cardNo: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {t('Amount')} <span className="text-red/90">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.001"
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm bg-gray-100"
                                                    value={formData.amount}
                                                    onChange={e => setFormData({ ...formData, amount: e.target.value })}
                                                    disabled={true}
                                                    readOnly
                                                    required
                                                />
                                            </div>
                                        </div>
                                        {formError && (
                                            <p className="mt-4 font-semibold text-sm text-red/90 text-center">
                                                {formError}
                                            </p>
                                        )}
                                        <div className="mt-5 sm:mt-6">
                                            <button
                                                type="submit"
                                                disabled={submitLoading}
                                                className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
                                            >
                                                {submitLoading ? (
                                                    <>
                                                        <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
                                                        {t('Submitting...')}
                                                    </>
                                                ) : (
                                                    t('Submit')
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
                {showErrDialog && (
                    <ErrorDialog
                        status={true}
                        title={t('Error')}
                        message={dialogMessage}
                        onClose={() => setShowErrDialog(false)}
                    />
                )}
                {showSucDialog && (
                    <SuccessDialog
                        status={true}
                        title={t('Success')}
                        message={dialogMessage}
                        onClose={() => setShowSucDialog(false)}
                    />
                )}

                {submitLoading && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                    </div>
                )}
            </Dialog>
        </>
    )
}
